import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { Plus, MoreHorizontal, Activity, Link2, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import WebhookModal from './WebhookModal';
import WebhookDeliveryLogs from './WebhookDeliveryLogs';

const API = `${API_BASE_URL}/api`;

const EVENT_LABELS = {
  'task.created': 'Tâche créée',
  'task.status_changed': 'Statut modifié',
  'task.assigned': 'Assignée',
  'task.overdue': 'En retard',
  'task.deleted': 'Supprimée',
  'deal.won': 'Deal gagné',
  'deal.lost': 'Deal perdu',
  'member.added': 'Membre ajouté',
  'automation.triggered': 'Automation',
};

const WebhooksSettings = ({ workspaceId, boardId, level, userRole }) => {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [logsWebhook, setLogsWebhook] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const canManage = userRole === 'owner' || userRole === 'admin';

  useEffect(() => {
    fetchWebhooks();
  }, [workspaceId, boardId]);

  const fetchWebhooks = async () => {
    try {
      setLoading(true);
      const params = { workspace_id: workspaceId };
      if (boardId) params.board_id = boardId;
      const res = await axios.get(`${API}/webhooks`, { params, withCredentials: true });
      setWebhooks(res.data);
    } catch {
      toast({ title: 'Erreur lors du chargement des webhooks', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (webhook, newActive) => {
    try {
      await axios.put(
        `${API}/webhooks/${webhook._id}`,
        { is_active: newActive },
        { withCredentials: true }
      );
      setWebhooks(prev => prev.map(w => w._id === webhook._id ? { ...w, is_active: newActive } : w));
    } catch {
      toast({ title: 'Erreur lors de la mise à jour', variant: 'destructive' });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await axios.delete(`${API}/webhooks/${deleteTarget._id}`, { withCredentials: true });
      setWebhooks(prev => prev.filter(w => w._id !== deleteTarget._id));
      toast({ title: 'Webhook supprimé' });
    } catch {
      toast({ title: 'Erreur lors de la suppression', variant: 'destructive' });
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const ownWebhooks = webhooks.filter(w => !w.inherited);
  const inheritedWebhooks = level === 'board' ? webhooks.filter(w => w.inherited) : [];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          {level === 'board' ? 'Webhooks du board' : 'Webhooks du workspace'}
        </h3>
        {canManage && (
          <Button
            size="sm"
            onClick={() => { setEditingWebhook(null); setShowModal(true); }}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Ajouter
          </Button>
        )}
      </div>

      {/* Own webhooks */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      ) : ownWebhooks.length === 0 ? (
        <div className="text-center py-10 text-gray-500 dark:text-gray-400">
          <Link2 className="h-8 w-8 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Aucun webhook configuré</p>
          <p className="text-xs mt-1">Connectez Doozum à Zapier, Make, Slack et plus.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {ownWebhooks.map(w => (
            <WebhookRow
              key={w._id}
              webhook={w}
              canManage={canManage}
              onToggle={handleToggle}
              onEdit={() => { setEditingWebhook(w); setShowModal(true); }}
              onLogs={() => setLogsWebhook(w)}
              onDelete={() => setDeleteTarget(w)}
            />
          ))}
        </div>
      )}

      {/* Inherited workspace webhooks (board view only) */}
      {inheritedWebhooks.length > 0 && (
        <div className="space-y-2 pt-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Hérités du workspace
          </p>
          {inheritedWebhooks.map(w => (
            <WebhookRow
              key={w._id}
              webhook={w}
              canManage={false}
              inherited
              onLogs={() => setLogsWebhook(w)}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      <WebhookModal
        open={showModal}
        onClose={() => setShowModal(false)}
        webhook={editingWebhook}
        workspaceId={workspaceId}
        boardId={boardId}
        level={level}
        onSaved={fetchWebhooks}
      />

      <WebhookDeliveryLogs
        open={!!logsWebhook}
        onClose={() => setLogsWebhook(null)}
        webhook={logsWebhook}
        workspaceId={workspaceId}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce webhook ?</AlertDialogTitle>
            <AlertDialogDescription>
              Les logs de livraison seront conservés 7 jours. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700">
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const WebhookRow = ({ webhook, canManage, inherited, onToggle, onEdit, onLogs, onDelete }) => (
  <div className={`flex items-center gap-3 p-3 rounded-lg border bg-white dark:bg-gray-800 dark:border-gray-700 ${!webhook.is_active ? 'opacity-60' : ''}`}>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium text-sm text-gray-900 dark:text-white">{webhook.name}</span>
        {inherited && (
          <Badge variant="secondary" className="text-xs">Workspace</Badge>
        )}
        {!webhook.is_active && (
          <Badge variant="outline" className="text-xs text-gray-400">Inactif</Badge>
        )}
      </div>
      <p className="text-xs text-gray-500 truncate mt-0.5">{webhook.url}</p>
      <div className="flex flex-wrap gap-1 mt-1.5">
        {(webhook.events || []).map(ev => (
          <Badge key={ev} variant="outline" className="text-xs py-0">
            {EVENT_LABELS[ev] || ev}
          </Badge>
        ))}
      </div>
    </div>

    {canManage && !inherited && (
      <Switch
        checked={webhook.is_active}
        onCheckedChange={val => onToggle(webhook, val)}
        className="shrink-0"
      />
    )}

    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onLogs}>
          <Activity className="h-4 w-4 mr-2" />
          Voir les logs
        </DropdownMenuItem>
        {canManage && !inherited && (
          <>
            <DropdownMenuItem onClick={onEdit}>Modifier</DropdownMenuItem>
            <DropdownMenuItem onClick={onDelete} className="text-red-600">
              Supprimer
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
);

export default WebhooksSettings;
